import React, { FC } from 'react';
import { formatDate, formatPatientId, formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';
import { Order } from 'models/order';
import {
    Alert,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import OrderStatusChip from '../order-status-chip.component';

type OngoingOrderDialogProps = {
    onClose: () => void;
    open: boolean;
    orders: Order[];
    operationType: string;
    onHandleOperation: () => void;
};

const OngoingOrderDialog: FC<OngoingOrderDialogProps> = ({
    onClose,
    open,
    orders,
    operationType,
    onHandleOperation,
}: OngoingOrderDialogProps) => {
    const { t: translate } = useTranslation(['orders', 'common', 'restoration-types']);

    const navigateToOrderDetails = (orderId: string) => {
        window.open(`/order-details/${orderId}/main-view`, '_blank');
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:ONGOING_ORDERS')}
            actions={
                <Button
                    sx={{ minWidth: 250 }}
                    variant="contained"
                    onClick={onHandleOperation}
                    data-test="operation-order-btn"
                >
                    {translate(`orders:${operationType}`)}
                </Button>
            }
        >
            <Box mb={1} mt={1} display="flex">
                <Box flexGrow={1}>
                    <Alert severity="info">{translate('orders:ONGOING_ORDER_INFO')}</Alert>
                </Box>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('common:PATIENT')}</TableCell>
                            <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                            <TableCell>{translate('common:DENTIST')}</TableCell>
                            <TableCell>{translate('common:STATUS')}</TableCell>
                            <TableCell>{translate('common:CREATED')}</TableCell>
                            <TableCell>{translate('orders:DELIVERY_DATE')}</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {orders.map((order) => (
                            <TableRow
                                key={order._id}
                                sx={{ cursor: 'pointer' }}
                                hover
                                onClick={() => navigateToOrderDetails(order._id)}
                            >
                                <TableCell className="mouseflow-mask">
                                    <Box>{formatPatientId(order.patient)}</Box>
                                    <Typography variant="caption">
                                        {formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {order.restorationTypes.map((type) => (
                                        <Box key={type}>{translate(`restoration-types:${type}`)}</Box>
                                    ))}
                                </TableCell>
                                <TableCell>{order.dentist?.fullName ?? NO_RESPONSIBLE_USER_PLACEHOLDER}</TableCell>
                                <TableCell>
                                    <OrderStatusChip status={order.status} />
                                </TableCell>
                                <TableCell>{formatDate(order.originalCreationDate, 'datetime')}</TableCell>
                                <TableCell>{formatDate(new Date(order.deliveryDetails.date), 'datetime')}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box mb={1} mt={1}>
                <Alert severity="info">
                    <strong>{translate(`orders:ARE_YOU_SURE_THAT_YOU_WANT_TO_${operationType}`)}</strong>
                </Alert>
            </Box>
        </DHDialog>
    );
};
export default OngoingOrderDialog;
